import React from "react"
import { CheckCircle2, Loader2, Circle, XCircle } from "lucide-react"
import { ErrorDisplay } from "./ErrorDisplay"

export interface TimelineStepProps {
  title: string
  status: 'idle' | 'pending' | 'error' | 'success'
  error?: string
  isLast: boolean
  isFallback?: boolean
  children?: React.ReactNode
}

export function TimelineStep({ title, status, error, isLast, isFallback = false, children }: TimelineStepProps) {
  const isComplete = status === 'success'
  const isInProgress = status === 'pending'
  const isError = status === 'error'

  return (
    <div className="flex gap-4">
      {/* Timeline rail */}
      <div className="flex flex-col items-center">
        {/* Circle indicator */}
        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 ${
          isComplete
            ? 'bg-green-500 border-green-500'
            : isError
            ? 'bg-red-500 border-red-500'
            : isInProgress
            ? 'bg-blue-500 border-blue-500'
            : 'bg-white border-gray-300'
        }`}>
          {isComplete ? (
            <CheckCircle2 className="w-5 h-5 text-white" />
          ) : isError ? (
            <XCircle className="w-5 h-5 text-white" />
          ) : isInProgress ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : (
            <Circle className="w-4 h-4 text-gray-300" />
          )}
        </div>
        {/* Connecting line */}
        {!isLast && (
          <div className={`w-0.5 flex-1 min-h-[60px] ${
            isComplete ? 'bg-green-500' : isError ? 'bg-red-500' : 'bg-gray-300'
          }`} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-8">
        <h3 className={`font-semibold text-base mb-2 ${
          isComplete ? 'text-gray-900' : isError ? 'text-red-600' : isInProgress ? 'text-blue-600' : 'text-gray-400'
        }`}>
          {title}
        </h3>
        {isFallback && isInProgress && (
          <div className="text-sm text-blue-600 flex items-center gap-2 mb-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Looking further back...
          </div>
        )}
        {isComplete && children && (
          <div className="text-sm text-gray-600">
            {children}
          </div>
        )}
        {isError && error && (
          <ErrorDisplay error={error} />
        )}
      </div>
    </div>
  )
}
